import type React from "react";
import {useNavigate, useParams} from "react-router-dom";

import {faSave, faTrash, faUpload} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {useSubmitLevelV2} from "~/api/hooks/useSubmitLevelV2";
import {
  useDeleteWipLevelV2,
  useUpdateWipLevelV2,
  useWipLevelV2,
} from "~/api/hooks/useWipLevelV2";
import CustomButton from "~/component/common/CustomButton";
import {useGameState} from "~/contexts/gamestate/useGameState";

import RenameButton from "./RenameButton";

interface EditMenuContentProps {
  closeModal: () => void;
}

const EditMenuContent: React.FC<EditMenuContentProps> = ({closeModal}) => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {data: level} = useWipLevelV2({id});
  const {getAllInstances} = useGameState();

  const {mutate: updateWipLevel, isPending: savePending} =
    useUpdateWipLevelV2();
  const {mutateAsync: deleteWipLevel, isPending: deletePending} =
    useDeleteWipLevelV2();
  const {mutateAsync: submitLevel} = useSubmitLevelV2();

  return (
    <div className="text-white w-full">
      {/* Top Section: Title linking back to create page */}
      <div className="mb-4 text-center">
        <CustomButton
          onClick={() => {
            navigate("/create");
            closeModal();
          }}
        >
          <h3 className="text-lg uppercase tracking-widest text-blue-500">
            CREATE
          </h3>
        </CustomButton>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-col gap-2">
        <CustomButton
          onClick={() => {
            if (!level || !id) return;
            updateWipLevel({
              key: {id},
              data: {
                ...level,
                data: getAllInstances(),
              },
            });
          }}
          className="relative flex items-center justify-center w-full py-2 bg-blue-500 hover:bg-blue-600 rounded transition"
          disabled={savePending}
        >
          <div className="absolute">
            <div className={`${savePending && "spinner"}`} />
          </div>
          <FontAwesomeIcon icon={faSave} size="lg" className="mr-2" />
          Save
        </CustomButton>
        <RenameButton />
        <CustomButton
          onClick={() => {
            if (level)
              submitLevel({
                data: {
                  ...level,
                  data: getAllInstances(),
                },
              }).then(() => navigate("/create"));
          }}
          className="flex items-center justify-center w-full py-2 bg-purple-500 hover:bg-purple-600 rounded transition"
        >
          <FontAwesomeIcon icon={faUpload} size="lg" className="mr-2" />
          Publish
        </CustomButton>
        <CustomButton
          onClick={() => {
            if (!level || !id) return;
            deleteWipLevel({
              key: {id},
            }).then(() => navigate("/create"));
          }}
          className="relative flex items-center justify-center w-full py-2 bg-red-500 hover:bg-red-600 rounded transition"
          disabled={deletePending}
        >
          <FontAwesomeIcon icon={faTrash} size="lg" className="mr-2" />
          <div className="absolute">
            <div className={`${deletePending && "spinner"}`} />
          </div>
          Delete
        </CustomButton>
      </div>
    </div>
  );
};

export default EditMenuContent;
